<template>
    <div>
        <PageHeader>
            {{ $t('settingsSpaces.title') }}

            <template #side>
                <b-button variant="primary" @click="triggerCreate">
                    {{ $t('create') }}
                </b-button>
            </template>
        </PageHeader>

        <b-modal
            :id="`modal-form-${modalId}`"
            ref="modal"
            :title="modalTitle"
            size="md"
            cancel-variant="light"
            ok-variant="primary"
            :cancel-title="$t('cancel')"
            :ok-title="$t('save')"
            :busy="submitting"
            static
            @ok.prevent="handleSubmit"
        >
            <b-overlay :show="submitting" opacity="0" class="h-100">
                <b-form @submit.prevent>
                    <ErrorMessage :value="formError" />

                    <FormValidator :validator="$v.form.name" form-group v-slot="slotProps" :label="$t('name')">
                        <b-form-input v-model.trim="form.name" :state="slotProps.state" />
                    </FormValidator>

                    <FormValidator :validator="$v.form.url" form-group v-slot="slotProps" :label="$t('url')">
                        <b-form-input v-model.trim="form.url" :state="slotProps.state" />
                    </FormValidator>

                    <FormValidator :validator="$v.form.category" form-group v-slot="slotProps" :label="$t('category')">
                        <b-form-select v-model="form.category" :options="categoryOptions" :state="slotProps.state" :disabled="submitting || categoryRequest.loading" />
                    </FormValidator>
                </b-form>
            </b-overlay>
        </b-modal>

        <DataTable :items="items" :fields="fields" :loading="loading" :error="error">
            <template #cell(action)="{ item }">
                <ButtonIcon size="sm" icon="edit-2" @click="triggerEdit(item)" />
            </template>
        </DataTable>
    </div>
</template>

<script>
    import DataTable from '@/components/common/DataTable.vue';
    import FormValidator from '@/components/common/FormValidator.vue';
    import { required, url } from 'vuelidate/lib/validators';
    import spaceService from '@/services/spaceService';

    export default {
        name: 'SettingsSpaces',
        components: {
            DataTable,
            FormValidator,
        },
        data() {
            return {
                modalId: 'spaces',
                form: {
                    name: '',
                    url: '',
                    category: null,
                },
                error: null,
                formError: null,
                submitting: false,
                selectedSpace: null,
                request: this.$req('space/spaces'),
                categoryRequest: this.$req('category/categories'),
            };
        },
        validations() {
            return {
                form: {
                    name: { required },
                    url: { required, url },
                    category: { required },
                },
            };
        },
        computed: {
            user() {
                return this.$store.getters['auth/getUser'];
            },
            items() {
                return (this.request.data?.items || []).map(space => ({
                    ...space,
                    categoryId: space.categories?.[0]?.id || null,
                    categoryName: space.categories?.[0]?.name || '',
                }));
            },
            categories() {
                return this.categoryRequest.data || [];
            },
            categoryOptions() {
                return this.categories.map(c => ({
                    value: c.id,
                    text: c.name,
                }));
            },
            loading() {
                return this.request.loading;
            },
            fields() {
                return [
                    { key: 'name', label: this.$t('name'), sortable: true },
                    { key: 'url', label: this.$t('url') },
                    { key: 'categoryName', label: this.$t('category') },
                    { key: 'action', label: '', sortable: false },
                ];
            },
            modalTitle() {
                return this.selectedSpace ? this.$t('settingsSpaces.editTitle') : this.$t('settingsSpaces.createTitle');
            },
        },
        methods: {
            fetchData() {
                this.request.get(this.user.id).catch(err => {
                    this.error = err;
                });
                this.categoryRequest.get().catch(err => {
                    this.formError = err;
                });
            },
            triggerEdit(space) {
                this.selectedSpace = structuredClone(space);
                this.form.name = space.name;
                this.form.url = space.url;
                this.form.category = space.categoryId;
                this.$v.$reset();
                this.$refs.modal.show();
                this.formError = null;
            },
            triggerCreate() {
                this.selectedSpace = null;
                this.form.name = '';
                this.form.url = '';
                this.form.category = null;
                this.$v.$reset();
                this.$refs.modal.show();
                this.formError = null;
            },
            async handleSubmit() {
                this.$v.$touch();
                if (this.$v.$invalid) return;

                this.submitting = true;
                const payload = {
                    name: this.form.name,
                    url: this.form.url,
                    categories: [this.form.category],
                    type: 1,
                };

                try {
                    if (this.selectedSpace) {
                        await spaceService.updateSpace(this.selectedSpace.id, payload);
                    } else {
                        await spaceService.createSpace(this.user.id, { ...payload, description: '...' });
                    }
                    this.fetchData();
                    this.$refs.modal.hide();
                    this.resetForm();
                } catch (err) {
                    this.formError = err;
                } finally {
                    this.submitting = false;
                }
            },
            resetForm() {
                this.selectedSpace = null;
                this.form.name = '';
                this.form.url = '';
                this.form.category = null;
                this.error = null;
                this.formError = null;
                this.$v.$reset();
            },
        },
        created() {
            this.fetchData();
        },
    };
</script>
